import { useContext, useEffect, useLayoutEffect, useRef } from "react";
import { __RouterContext as RouterContext } from "react-router";

import { onScrollRestoreRelease, returnMemory as defaultMemory } from "./returnMemory";

const RESTORE_TIMEOUT_MS = 4000;

// Type du chargement du document (« navigate », « reload », « back_forward »).
const documentNavigationType = () => {
  try {
    return window.performance?.getEntriesByType?.("navigation")?.[0]?.type || null;
  } catch (_error) {
    return null;
  }
};
// Durée pendant laquelle la position retrouvée est tenue pendant que la page
// finit de se construire.
const SETTLE_MS = 800;

/**
 * Branche la mémoire de retour sur le routeur : chaque adresse affichée est
 * retenue (y compris les `history.replace` d'une liste qui écrit ses filtres
 * dans l'adresse), la position de défilement suit le défilement, et un retour
 * (fil d'Ariane, « Retour aux … », bouton précédent du navigateur) restaure
 * la position une fois la liste rechargée. Sans routeur (catalogue, tests),
 * le hook ne fait rien.
 */
export default function useReturnMemory(memory = defaultMemory) {
  const router = useContext(RouterContext);
  const location = router?.location || null;
  const action = router?.history?.action;
  const locationRef = useRef(location);
  locationRef.current = location;
  // Pendant une restauration, les défilements subis (page vide qui se
  // recharge) ne doivent pas écraser la position retenue.
  const restoringRef = useRef(false);
  const pathname = location?.pathname;
  const search = location?.search;

  useEffect(() => {
    if (typeof window === "undefined" || !router) return undefined;
    let frame = 0;
    const page = () => document.scrollingElement || document.documentElement;
    let lastHeight = page().scrollHeight;
    let lastTop = window.scrollY;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const height = page().scrollHeight;
        const top = window.scrollY;
        // Une page qui raccourcit (la liste remplacée par la fiche, un
        // squelette) ramène le défilement vers le haut sans geste de
        // l'utilisateur : ce recul n'est pas une position à retenir.
        const clampedByShrink = height < lastHeight && top < lastTop && top >= height - window.innerHeight - 2;
        lastHeight = height;
        lastTop = top;
        if (clampedByShrink || restoringRef.current) return;
        memory.rememberScroll(locationRef.current, top);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [memory, router]);

  // Avant le premier rendu de la nouvelle adresse : on lit la position à
  // restaurer avant que la page rechargée ne la fasse varier.
  useLayoutEffect(() => {
    if (!pathname) return undefined;
    const current = { pathname, search: search || "" };
    memory.arrive(current, action, { documentNavigation: documentNavigationType() });
    memory.remember(current);
    const isReturn = memory.takeReturn(current) || action === "POP";
    const top = isReturn ? memory.scrollFor(current) : null;
    if (!top || typeof window === "undefined") return undefined;
    restoringRef.current = true;
    // La page réaffiche ses lignes (tout de suite depuis le cache de lecture,
    // sinon après un squelette) : on attend qu'elle soit assez haute, puis on
    // tient la position le temps qu'elle se stabilise (images, sections
    // chargées après coup). Le premier geste de l'utilisateur rend la main.
    const started = Date.now();
    let reachedAt = 0;
    let frame = 0;
    let released = false;
    const release = () => {
      released = true;
      restoringRef.current = false;
    };
    const interactions = ["wheel", "touchstart", "keydown", "mousedown"];
    interactions.forEach((name) => window.addEventListener(name, release, { passive: true, once: true }));
    const stopListeningRelease = onScrollRestoreRelease(release);
    const restore = () => {
      if (released) return;
      const page = document.scrollingElement || document.documentElement;
      const elapsed = Date.now() - started;
      const reachable = page.scrollHeight - window.innerHeight >= top;
      if (reachable || elapsed > RESTORE_TIMEOUT_MS) {
        if (Math.abs(window.scrollY - top) > 1) window.scrollTo(0, top);
        if (!reachedAt) reachedAt = Date.now();
        if (Date.now() - reachedAt > SETTLE_MS || elapsed > RESTORE_TIMEOUT_MS) {
          release();
          return;
        }
      }
      frame = window.requestAnimationFrame(restore);
    };
    restore();
    return () => {
      window.cancelAnimationFrame(frame);
      interactions.forEach((name) => window.removeEventListener(name, release));
      stopListeningRelease();
      restoringRef.current = false;
    };
  }, [action, memory, pathname, search]);
}

/**
 * Branchement de la mémoire de retour dans l'application (couche `app` : le
 * shell React reste indépendant du routeur). Monté une fois autour des routes /v2.
 */
export function ReturnMemoryRecorder() {
  useReturnMemory();
  return null;
}
